import {
  BillingInterval,
  billingPrice,
} from "./BillingInterval";
import { previewPlan, undoPlanChange } from "./api";
import { useContext } from "react";
import { AppContext } from "./context";
import { pollForSubscriptionUpdate } from "./utils/pollForSubscriptionUpdate";
import {
  Text,
  Button,
  Flex,
  HStack,
  VStack,
  useClipboard,
  useToast,
  Box,
  Heading,
  Divider,
  Container,
  InputGroup,
  Input,
  InputRightElement,
  useColorModeValue,
  useDisclosure,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Badge,
} from "@chakra-ui/react";
import React from "react";
import {
  createManageSubscriptionSession,
  updateTier,
  rotateApiKey,
  reactivateSubscription,
  getSubscriptionDetails,
  getSession,
  getTrialEligibility,
  createTrialCheckoutSession,
  convertTrialNow,
  getAccountPlans,
  TrialEligibilityEntry,
} from "./api";
import type { AccountPlan } from "./api";
import {
  Tier,
  Type,
  Subscription,
  StripeSubscriptionDetail,
  isTrialing,
} from "./types";
import { datadogRum } from "@datadog/browser-rum";
import { useMobile } from "./hooks";
import { useNavigate } from "react-router-dom";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  isAllAccessType,
  ALL_ACCESS_PRICE_MAP,
  ALL_ACCESS_PRICE_LABEL_MAP,
  ALL_ACCESS_DISPLAY_PRICE,
  CURRENT_ALL_ACCESS_TYPE,
} from "./allAccess";
import {
  getStoryStatsConversionValue,
  getStoryStatsProductInfo,
  getStoryStatsPriceLabel,
  isStoryStatsType,
} from "./storyStatsPricing";

import {
  ActiveSubscriptions,
  AllAccessBanner,
  SubscriptionTable,
  SportDetailModal,
} from "./PricingComponents";
import { buildTrialUpgradeConfirmationContent } from "./trialUpgradeConfirmation";
import { BillingAttention } from "./BillingAttention";
import { TrialCancellationDialog, TrialCancellationAction } from "./TrialCancellationDialog";
import { changeTrialCancellation } from "./api";
import { BillingHistory } from "./BillingHistory";

const isMobileDevice = () =>
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

type HomePage = "account" | "subscriptions";

type HomeProps = {
  page?: HomePage;
};

export const Home = ({ page = "account" }: HomeProps) => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AppContext);
  if (user?.email) {
    datadogRum.setUser({
      email: user.email,
      apiKey: user.apiKey,
    });
  }
  const toast = useToast();
  const { onCopy, setValue, hasCopied } = useClipboard("");
  const isSmallScreen = useMobile();
  const isAccountPage = page === "account";
  const isSubscriptionsPage = page === "subscriptions";
  const isEnterprise = !!user?.isEnterprise;

  // Helper to get subscription tier for a sport
  const getSubTier = (type: Type): Tier => {
    const sub = user?.subscriptions?.find((s) => s.type === type);
    return (sub?.tier as Tier) || "free";
  };

  // Check for all-access subscription - find the one with paid+ tier (user may have legacy free entries)
  const allAccessSub =
    user?.subscriptions?.find(
      (s) => isAllAccessType(s.type) && s.tier === "paid+",
    ) || user?.subscriptions?.find((s) => isAllAccessType(s.type));
  const hasAllAccess = allAccessSub?.tier === "paid+";

  // Build subscriptions array for SubscriptionTable (with defaults)
  // Filter out products managed in separate apps
  const subscriptions: Subscription[] = (user?.subscriptions || []).filter(
    (s) => s.type !== "lab" && (s.type as string) !== "chat",
  );

  const [billingProducts, setBillingProducts] = React.useState<{
    customer: string | null | undefined;
    types: Type[];
  }>({ customer: undefined, types: [] });
  const onBillingTypesChange = React.useCallback(
    (types: Type[]) => {
      setBillingProducts({ customer: user?.stripeCustomerId, types });
    },
    [user?.stripeCustomerId],
  );
  const billingAttentionTypes =
    !isEnterprise && billingProducts.customer === user?.stripeCustomerId
      ? billingProducts.types
      : [];

  const [billingInterval, setBillingInterval] = React.useState<BillingInterval>(
    () =>
      new URLSearchParams(window.location.search).get("billing_interval") ===
      "year"
        ? "year"
        : "month",
  );
  const [planPreview, setPlanPreview] = React.useState<any>(null);
  const [previewError, setPreviewError] = React.useState("");
  const [selected, setSelected] = React.useState<{
    tier: Tier;
    type: Type;
  } | null>(null);
  const [selectedSport, setSelectedSport] = React.useState<Type | null>(null);
  const [isLoading, setLoading] = React.useState(false);
  const [pendingTrial, setPendingTrial] = React.useState<{
    type: Type;
    cameFromSportModal: boolean;
  } | null>(null);
  const [isStartingTrial, setIsStartingTrial] = React.useState(false);
  const [pendingTrialConversion, setPendingTrialConversion] = React.useState<{
    type: Type;
    cameFromSportModal: boolean;
  } | null>(null);
  const [isConfirmingTrialConversion, setIsConfirmingTrialConversion] =
    React.useState(false);
  const sportModalDisclosure = useDisclosure();
  const rotateDisclosure = useDisclosure();
  const [isRotating, setIsRotating] = React.useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = React.useState<
    StripeSubscriptionDetail[]
  >([]);
  const annualChange =
    !!selected &&
    selected.tier !== "free" &&
    !isStoryStatsType(selected.type) &&
    !!subscriptionDetails.find((d) => d.type === selected.type) &&
    (billingInterval === "year" ||
      subscriptionDetails.find((d) => d.type === selected.type)
        ?.billingInterval === "year");
  React.useEffect(() => {
    let active = true;
    setPlanPreview(null);
    setPreviewError("");
    if (annualChange && selected)
      previewPlan(selected.tier, selected.type, billingInterval)
        .then((p) => {
          if (active) setPlanPreview(p);
        })
        .catch((e) => {
          if (active)
            setPreviewError(
              e.response?.data?.error || "Unable to preview this change.",
            );
        });
    return () => {
      active = false;
    };
  }, [selected, billingInterval, annualChange]);
  const [accountPlans, setAccountPlans] = React.useState<AccountPlan[]>([]);
  const [trialEligibility, setTrialEligibility] = React.useState<
    TrialEligibilityEntry[]
  >([]);
  const [convertingTrialTypes, setConvertingTrialTypes] = React.useState<
    Type[]
  >([]);

  const [trialCancellation, setTrialCancellation] = React.useState<{ subscription: Subscription; action: TrialCancellationAction } | null>(null);
  const [cancellingTrial, setCancellingTrial] = React.useState(false);
  const [trialCancellationError, setTrialCancellationError] = React.useState("");

  const trialEndIsPast = (trialEnd: string | null): boolean =>
    !!trialEnd && new Date(trialEnd).getTime() <= Date.now();

  const persistedBillingTrialConversionPending = (sub?: Subscription) =>
    !!sub &&
    sub.tier === "paid+" &&
    sub.trialSource === "billing_trial" &&
    !!sub.trialStartedAt &&
    trialEndIsPast(sub.trialEnd);

  const openOverlaySub = subscriptions.find(
    (s) =>
      s.trialSource === "paid_upgrade_overlay" &&
      ["active", "cancel_scheduled", "converting", "payment_pending"].includes(
        s.trialConversionStatus || "",
      ),
  );
  const hasOverlayBillingLock = !!openOverlaySub;
  const isSameActiveOverlayTrial = (type: Type) =>
    openOverlaySub?.type === type &&
    ["active", "cancel_scheduled"].includes(openOverlaySub.trialConversionStatus || "") &&
    isTrialing(openOverlaySub);

  const isTrialConversionPending = (type: Type): boolean => {
    const sub = subscriptions.find((s) => s.type === type);
    return (
      convertingTrialTypes.includes(type) ||
      (!sub?.trialPendingAction && persistedBillingTrialConversionPending(sub)) ||
      (!!sub && !sub.trialPendingAction &&
        ["paid_upgrade_overlay", "billing_trial"].includes(sub.trialSource || "") &&
        ["converting", "payment_pending"].includes(
          sub.trialConversionStatus || "",
        ))
    );
  };

  const isBillingActionLocked = (type: Type): boolean => {
    if (hasOverlayBillingLock) {
      return !isSameActiveOverlayTrial(type);
    }
    return !!subForType(type)?.trialPendingAction || isTrialConversionPending(type);
  };

  const refreshSubscriptionDetails = React.useCallback(async () => {
    setSubscriptionDetails(await getSubscriptionDetails());
  }, []);

  // Fetch subscription details for cancellation state
  React.useEffect(() => {
    if (!isSubscriptionsPage || isEnterprise || !user?.stripeCustomerId) return;
    getSubscriptionDetails()
      .then(setSubscriptionDetails)
      .catch(() => {});
  }, [isSubscriptionsPage, isEnterprise, user?.stripeCustomerId]);

  // Fetch trial eligibility for every trial-eligible type at once.
  // Refetched when the user's email identity changes; handlers refetch
  // explicitly after trial start/cancel/end transitions so trial_used is
  // reflected without a full page reload.
  React.useEffect(() => {
    if (!isSubscriptionsPage || isEnterprise || !user?.email) return;
    getTrialEligibility()
      .then(setTrialEligibility)
      .catch(() => setTrialEligibility([]));
  }, [isSubscriptionsPage, isEnterprise, user?.email]);

  React.useEffect(() => {
    if (!isSubscriptionsPage || isEnterprise || !user?.email) return;
    getAccountPlans()
      .then(setAccountPlans)
      .catch(() => setAccountPlans([]));
  }, [isSubscriptionsPage, isEnterprise, user?.email]);

  React.useEffect(() => {
    if (user?.apiKey) {
      setValue(user?.apiKey);
    }
  }, [user?.apiKey, setValue]);

  React.useEffect(() => {
    if (isSubscriptionsPage) return;
    setSelected(null);
    setSelectedSport(null);
    setPendingTrial(null);
    setPendingTrialConversion(null);
    sportModalDisclosure.onClose();
  }, [isSubscriptionsPage, sportModalDisclosure]);

  React.useEffect(() => {
    if (isAccountPage) return;
    rotateDisclosure.onClose();
  }, [isAccountPage, rotateDisclosure]);

  // If stripe_session_id is set, the customer arrived here after successfully checking out.
  // We need to call an endpoint that will fetch the stripe customer based on the session_id and update
  // the users.stripe_customer_id column.
  const query = new URLSearchParams(window.location.search);

  // We set this in 'succes_url' for stripe redirect after successful checkout for google ad conversion tracking
  // This is only necessary when a redirect is required (e.g. payment flows that require authorization on a different page).
  // Normal credit card payments won't hit this flow.
  const stripeRedirectStatus = query.get("redirect_status");
  const purchasedTier = query.get("tier");
  const purchasedType = query.get("type");

  React.useEffect(() => {
    if (!isAccountPage) return;
    const isSuccess = stripeRedirectStatus === "succeeded";

    if (isSuccess) {
      getSession()
        .then(setUser)
        .catch(() => {});

      toast({
        title: "Tier Upgraded",
        description: "Your subscription is being activated.",
        status: "success",
        isClosable: true,
        duration: null,
      });

      const dollars =
        purchasedType && ALL_ACCESS_PRICE_MAP[purchasedType]
          ? ALL_ACCESS_PRICE_MAP[purchasedType]
          : purchasedType
            ? (getStoryStatsConversionValue(purchasedType) ??
              (purchasedTier === "paid" ? 9.99 : 39.99))
            : purchasedTier === "paid"
              ? 9.99
              : 39.99;
      (window as any).gtag("event", "conversion", {
        send_to: "AW-16760575486/17bXCKTZhOQZEP6riLg-",
        value: dollars,
        currency: "USD",
        transaction_id: user?.email,
      });

      datadogRum.addAction("stripe_callback", {
        redirect: true,
        tier: purchasedTier,
        purchasedTier,
        query: query.toString(),
        cookie: document.cookie,
        email: user?.email,
      });
    } else if (stripeRedirectStatus?.includes("fail")) {
      toast({
        title: "Payment Failed",
        description:
          "Please try again or contact support at hello@balldontlie.io for help.",
        status: "error",
        isClosable: true,
        duration: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountPage, stripeRedirectStatus, purchasedTier, purchasedType]);

  const onNewCustomer = async (tier: Tier, type: Type) => {
    navigate(
      `/checkout?tier=${tier === "paid+" ? "paid_plus" : tier}&type=${type}&billing_interval=${isStoryStatsType(type) ? "month" : billingInterval}`,
    );
  };

  const onRotateApiKey = async () => {
    setIsRotating(true);
    try {
      const updatedUser = await rotateApiKey();
      setUser(updatedUser);
      toast({
        title: "API Key Rotated",
        description:
          "Your new API key is active. The old key has been permanently invalidated.",
        status: "success",
        isClosable: true,
        duration: 5000,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to rotate API key. Please try again.",
        status: "error",
        isClosable: true,
      });
      setIsRotating(false);
      return;
    }
    setIsRotating(false);
    rotateDisclosure.onClose();
  };

  // Trial-eligible types (for the CTA swap).
  const trialEligibleTypes = React.useMemo(
    () =>
      new Set<Type>(
        trialEligibility.filter((e) => e.eligible).map((e) => e.type),
      ),
    [trialEligibility],
  );

  const isGoatTrialEligibleFor = (type: Type): boolean =>
    trialEligibleTypes.has(type);

  // Returns the subscription row for a given type (used for trial state).
  const subForType = (type: Type): Subscription | undefined =>
    subscriptions.find((s) => s.type === type);

  // Opens the trial confirmation modal. The actual trial start happens in
  // `startTrial` once the user confirms.
  const onRequestStartTrial = (type: Type, cameFromSportModal: boolean) => {
    if (cameFromSportModal) {
      sportModalDisclosure.onClose();
    }
    setPendingTrial({ type, cameFromSportModal });
  };

  const onCancelTrialConfirmation = () => {
    if (!pendingTrial) return;
    const { cameFromSportModal } = pendingTrial;
    setPendingTrial(null);
    if (cameFromSportModal && selectedSport) {
      sportModalDisclosure.onOpen();
    }
  };

  const onConfirmTrial = async () => {
    if (!pendingTrial) return;
    const { type, cameFromSportModal } = pendingTrial;
    setIsStartingTrial(true);
    try {
      await startTrial(type);
    } finally {
      setIsStartingTrial(false);
      setPendingTrial(null);
      if (cameFromSportModal) setSelectedSport(null);
    }
  };

  // Starts a trial. Backend returns either { clientSecret } (new Checkout) or
  // { upgraded: true } (paid-upgrade overlay). Latter polls in place.
  const startTrial = async (type: Type) => {
    try {
      const response = await createTrialCheckoutSession(type, billingInterval);
      if ("clientSecret" in response && response.clientSecret) {
        // New Checkout — navigate to the checkout page with trial flag.
        // We pass the clientSecret via sessionStorage so StripeCheckout can
        // render without a second API call.
        sessionStorage.setItem(
          "bdl_trial_client_secret",
          response.clientSecret,
        );
        sessionStorage.setItem("bdl_trial_type", type);
        sessionStorage.setItem("bdl_trial_interval", billingInterval);
        navigate(
          `/checkout?trial=true&type=${type}&billing_interval=${billingInterval}`,
        );
        return;
      }
      // Paid-upgrade overlay: poll for the local GOAT access change.
      toast({
        title: "Trial Starting",
        description: "Your 7-day GOAT trial is activating.",
        status: "info",
        isClosable: true,
        duration: 5000,
      });
      try {
        const { user: updatedUser } = await pollForSubscriptionUpdate(
          "paid+",
          type,
        );
        setUser(updatedUser);
        const details = await getSubscriptionDetails().catch(() => []);
        setSubscriptionDetails(details);
        const elig = await getTrialEligibility().catch(() => []);
        setTrialEligibility(elig);
      } catch {
        // Polling timeout is fine — webhook will land eventually.
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || "Unable to start trial.";
      toast({
        title: "Trial Unavailable",
        description: msg,
        status: "warning",
        isClosable: true,
        duration: 8000,
      });
    }
  };

  const requestTrialCancellation = (type: Type, undo = false) => {
    const subscription = subForType(type);
    if (!subscription?.trialEnd) return;
    const scheduled = subscription.trialConversionStatus === "cancel_scheduled" || subscriptionDetails.find(d => d.type === type)?.cancelAtPeriodEnd;
    setTrialCancellationError("");
    const pendingAction = subscription.trialPendingAction;
    if (pendingAction === "convert") return;
    setTrialCancellation({ subscription, action: pendingAction || (undo ? "undo" : scheduled ? "immediate" : "end_of_trial") });
  };
  const onTrialCancel = (type: Type) => requestTrialCancellation(type);
  const onTrialReactivate = (type: Type) => requestTrialCancellation(type, true);
  const confirmTrialCancellation = async (action: TrialCancellationAction) => {
    if (!trialCancellation) return;
    setCancellingTrial(true);
    setTrialCancellationError("");
    try {
      const { subscription } = trialCancellation;
      await changeTrialCancellation(subscription.type, subscription.trialEnd!, action);
      const [freshUser, details, eligibility] = await Promise.all([getSession(), getSubscriptionDetails(), getTrialEligibility()]);
      setUser(freshUser);
      setSubscriptionDetails(details);
      setTrialEligibility(eligibility);
      setTrialCancellation(null);
      toast({ title: action === "undo" ? "Cancellation undone" : action === "immediate" ? "Trial ended" : "Trial cancellation scheduled", status: "success", isClosable: true });
    } catch (e: any) {
      setTrialCancellationError(e?.response?.data?.error || "Unable to update your trial. Please try again.");
      // A failed response can leave the original action pending. Keep its intent
      // in the normal dialog, just as a page reload would.
      const freshUser = await getSession().catch(() => null);
      if (freshUser) {
        setUser(freshUser);
        const subscription = freshUser.subscriptions.find(s => s.type === trialCancellation.subscription.type);
        const pendingAction = subscription?.trialPendingAction;
        if (subscription?.trialEnd && pendingAction && pendingAction !== "convert") {
          setTrialCancellation({ subscription, action: pendingAction });
        }
      }
    } finally { setCancellingTrial(false); }
  };

  const pollForTrialConversion = async (type: Type) => {
    for (let i = 0; i < 15; i++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const freshUser = await getSession();
      setUser(freshUser);
      const sub = freshUser.subscriptions?.find((s) => s.type === type);
      if (sub?.tier === "paid+" && sub.trialEnd === null) {
        return true;
      }
    }
    const freshUser = await getSession();
    setUser(freshUser);
    return false;
  };

  const onRequestTrialConvert = (type: Type, cameFromSportModal: boolean) => {
    if (cameFromSportModal) {
      sportModalDisclosure.onClose();
    }
    setPendingTrialConversion({ type, cameFromSportModal });
  };

  const onDismissTrialConversion = () => {
    if (!pendingTrialConversion) return;
    const { cameFromSportModal } = pendingTrialConversion;
    setPendingTrialConversion(null);
    if (cameFromSportModal && selectedSport) {
      sportModalDisclosure.onOpen();
    }
  };

  const convertTrial = async (type: Type) => {
    setConvertingTrialTypes((types) =>
      types.includes(type) ? types : [...types, type],
    );
    try {
      const response = await convertTrialNow(type);
      const details = await getSubscriptionDetails().catch(() => []);
      setSubscriptionDetails(details);

      if (response.status === "payment_pending") {
        toast({
          title: "Payment Pending",
          description:
            "Your upgrade is pending payment confirmation. Update your payment method if needed.",
          status: "warning",
          isClosable: true,
          duration: 8000,
        });
        const freshUser = await getSession();
        setUser(freshUser);
        return;
      }

      const confirmed = await pollForTrialConversion(type);
      getSubscriptionDetails()
        .then(setSubscriptionDetails)
        .catch(() => {});
      getTrialEligibility()
        .then(setTrialEligibility)
        .catch(() => {});

      if (confirmed || response.status === "converted") {
        toast({
          title: "Trial Upgraded",
          description: "Your GOAT subscription is now active.",
          status: "success",
          isClosable: true,
          duration: 5000,
        });
      } else {
        toast({
          title: "Upgrade Processing",
          description:
            "Your upgrade is being processed. It may take a few minutes to appear.",
          status: "info",
          isClosable: true,
          duration: null,
        });
      }
    } catch (e: any) {
      const backendMessage = e?.response?.data?.error;
      toast({
        title: "Unable to Upgrade Trial",
        description:
          backendMessage ||
          "Unable to upgrade this trial right now. Please try again.",
        status: "error",
        isClosable: true,
        duration: 8000,
      });
    } finally {
      setConvertingTrialTypes((types) => types.filter((t) => t !== type));
    }
  };

  const onConfirmTrialConversion = async () => {
    if (!pendingTrialConversion) return;
    const { type, cameFromSportModal } = pendingTrialConversion;
    setIsConfirmingTrialConversion(true);
    try {
      await convertTrial(type);
    } finally {
      setIsConfirmingTrialConversion(false);
      setPendingTrialConversion(null);
      if (cameFromSportModal) setSelectedSport(null);
    }
  };

  const pendingTrialConversionContent = pendingTrialConversion
    ? buildTrialUpgradeConfirmationContent({
        type: pendingTrialConversion.type,
        subscription: subForType(pendingTrialConversion.type),
        accountPlans,
        billingInterval: subscriptionDetails.find(
          (d) => d.type === pendingTrialConversion.type,
        )?.trialBillingInterval,
        currentBillingInterval: subscriptionDetails.find(
          (d) => d.type === pendingTrialConversion.type,
        )?.billingInterval,
      })
    : null;

  const onReactivate = async (type: Type) => {
    try {
      await reactivateSubscription(type);
      toast({
        title: "Subscription Reactivated",
        description: "Your subscription has been reactivated.",
        status: "success",
        isClosable: true,
        duration: 5000,
      });
      // Refresh subscription details
      const details = await getSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch {
      toast({
        title: "Error",
        description: "Failed to reactivate subscription. Please try again.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const onManageSport = (type: Type) => {
    setBillingInterval(
      subscriptionDetails.find((detail) => detail.type === type)?.billingInterval || "month",
    );
    setSelectedSport(type);
    sportModalDisclosure.onOpen();
  };

  const onManagePaymentDetails = async () => {
    const url = await createManageSubscriptionSession("billing_overview");
    if (isMobileDevice()) {
      window.location.href = url;
    } else {
      window.open(url);
    }
  };

  const modalContent = () => {
    if (!selected) {
      return null;
    }

    if (selected.tier === "free") {
      return "Your subscription will remain active until the end of your current billing period. You can reactivate anytime before then.";
    }

    if (billingInterval === "year" && !isStoryStatsType(selected.type))
      return `Subscribe to ${isAllAccessType(selected.type) ? "ALL-ACCESS" : selected.tier === "paid" ? "ALL-STAR" : "GOAT"}, billed annually. ${isAllAccessType(selected.type) ? "Your covered individual subscriptions end after payment succeeds." : ""}`;
    const storyStatsInfo = getStoryStatsProductInfo(selected.type);
    if (storyStatsInfo && selected.tier === "paid") {
      return `Subscribe to ${storyStatsInfo.displayName} (${getStoryStatsPriceLabel(
        selected.type,
      )}) for pregame, live, and postgame narratives.`;
    }

    if (isAllAccessType(selected.type)) {
      return `Upgrade to ALL-ACCESS (${ALL_ACCESS_DISPLAY_PRICE}) to get access to all endpoints for every sport. This will cancel all other active subscriptions immediately.`;
    }

    const currTier =
      user?.subscriptions?.find((s) => s.type === selected.type)?.tier ||
      "free";

    if (selected.tier === "paid+" && currTier === "paid") {
      return "Upgrade to GOAT ($39.99) to get access to all endpoints for this sport.";
    }

    if (selected.tier === "paid+" && (currTier === "free" || !currTier)) {
      return "Subscribe to GOAT ($39.99) to get access to all endpoints for this sport.";
    }

    if (selected.tier === "paid" && (currTier === "free" || !currTier)) {
      return "Subscribe to ALL-STAR ($9.99) to get access to more endpoints for this sport.";
    }

    if (selected.tier === "paid" && currTier === "paid+") {
      return "Downgrade to ALL-STAR ($9.99) to get access to fewer endpoints for this sport.";
    }
  };

  const bgColor = useColorModeValue("gray.50", "transparent");
  const sectionHeaderColor = useColorModeValue("gray.700", "gray.300");
  const dividerBorderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");

  const allAccessNeedsAttention = billingAttentionTypes.some(isAllAccessType);
  const allAccessBanner = (
    <VStack align="stretch" spacing={4}>
      <AllAccessBanner
        billingInterval={billingInterval}
        onBillingIntervalChange={setBillingInterval}
        onSwitchToAnnual={() => { setBillingInterval("year"); setSelected({type: CURRENT_ALL_ACCESS_TYPE, tier:"paid+"}); }}
        onUndoPlanChange={async () => { await undoPlanChange(CURRENT_ALL_ACCESS_TYPE); setSubscriptionDetails(await getSubscriptionDetails()); }}
        isSubscribed={hasAllAccess}
        subscriptionType={allAccessSub?.type}
        subscriptionDetails={subscriptionDetails}
        onSubscribe={() =>
          setSelected({
            tier: "paid+",
            type: CURRENT_ALL_ACCESS_TYPE,
          })
        }
        onCancel={() =>
          setSelected({
            tier: "free",
            type: allAccessSub?.type || CURRENT_ALL_ACCESS_TYPE,
          })
        }
        onReactivate={() =>
          allAccessSub?.type ? onReactivate(allAccessSub.type) : undefined
        }
        isTrialEligible={isGoatTrialEligibleFor(CURRENT_ALL_ACCESS_TYPE)}
        onStartTrial={() => onRequestStartTrial(CURRENT_ALL_ACCESS_TYPE, false)}
        trialSubscription={subForType(CURRENT_ALL_ACCESS_TYPE)}
        onTrialCancel={() => onTrialCancel(CURRENT_ALL_ACCESS_TYPE)}
        onTrialReactivate={() => onTrialReactivate(CURRENT_ALL_ACCESS_TYPE)}
        onTrialConvert={() =>
          onRequestTrialConvert(CURRENT_ALL_ACCESS_TYPE, false)
        }
        isBillingLocked={isBillingActionLocked(
          allAccessSub?.type || CURRENT_ALL_ACCESS_TYPE,
        )}
        isTrialConversionPending={isTrialConversionPending(
          allAccessSub?.type || CURRENT_ALL_ACCESS_TYPE,
        )}
      />

    </VStack>
  );

  return (
    <Box minH="100vh" w="100%" bg={bgColor} pt={12} pb={20}>
      <Container maxW={isSmallScreen ? "container.sm" : "container.lg"} px={6}>
        <VStack spacing={12} align="stretch">
          {/* ── Section 1: API Key ─────────────────────────────── */}
          {isAccountPage && (
            <Box>
              <HStack mb={3}>
                <Heading size="sm" color={sectionHeaderColor} fontWeight="600">
                  API Key
                </Heading>
              </HStack>
              <Divider borderColor={dividerBorderColor} mb={5} />

              <Box
                bg={cardBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={cardBorder}
                p={6}
              >
                <Text
                  mb={2}
                  fontWeight="500"
                  fontSize="xs"
                  color={mutedText}
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Your API Key
                </Text>
                <InputGroup size="md">
                  <Input
                    pr="4.5rem"
                    value={user?.apiKey || ""}
                    isReadOnly
                    fontFamily="mono"
                    fontSize="sm"
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={onCopy}
                      colorScheme={hasCopied ? "green" : "brand"}
                    >
                      {hasCopied ? "Copied" : "Copy"}
                    </Button>
                  </InputRightElement>
                </InputGroup>

                <HStack spacing={4} mt={3}>
                  <Link
                    fontSize="sm"
                    color="red.500"
                    onClick={rotateDisclosure.onOpen}
                    _hover={{ textDecoration: "underline" }}
                    cursor="pointer"
                  >
                    Rotate key
                  </Link>
                </HStack>
              </Box>
            </Box>
          )}

          {isSubscriptionsPage && !isEnterprise && user?.stripeCustomerId && (
            <BillingAttention
              key={user.stripeCustomerId}
              onRefresh={refreshSubscriptionDetails}
              onTypesChange={onBillingTypesChange}
              onChoosePlan={onManageSport}
            />
          )}

          {/* ── Section 2: Active Subscriptions ────────────────── */}
          {isSubscriptionsPage && (
            <>
              <ActiveSubscriptions
                subscriptions={subscriptions}
                subscriptionDetails={subscriptionDetails}
                onUndoPlanChange={async (type) => { await undoPlanChange(type); setSubscriptionDetails(await getSubscriptionDetails()); }}
                onManage={onManageSport}
                onReactivate={(type) => onReactivate(type)}
                onTrialCancel={onTrialCancel}
                onTrialReactivate={onTrialReactivate}
                onTrialConvert={(type) => onRequestTrialConvert(type, false)}
                isBillingActionLocked={isBillingActionLocked}
                isTrialConversionPending={isTrialConversionPending}
              />
            </>
          )}

          {isSubscriptionsPage && allAccessNeedsAttention && hasAllAccess && (
            <Box as="section" aria-label="Active ALL-ACCESS subscription">
              {allAccessBanner}
            </Box>
          )}

          {/* ── Section 3: Available Sports ──────────────────────── */}
          {isSubscriptionsPage &&
            !(allAccessNeedsAttention && hasAllAccess) && (
              <Box as="section" aria-label="Available Sports">
                <HStack mb={3}>
                  <Heading
                    size="sm"
                    color={sectionHeaderColor}
                    fontWeight="600"
                  >
                    Available Sports
                  </Heading>
                </HStack>
                <Divider borderColor={dividerBorderColor} mb={5} />

                <VStack spacing={5} align="stretch">
                  {/* All-Access Banner (both subscribed and upsell) */}
                  {!allAccessNeedsAttention && allAccessBanner}

                  {/* Per-sport subscription table (only for non-ALL-ACCESS users) */}
                  {!hasAllAccess && (
                    <SubscriptionTable
                      excludedTypes={billingAttentionTypes}
                      subscriptions={subscriptions}
                      subscriptionDetails={subscriptionDetails}
                      onSportClick={onManageSport}
                      isMobile={isSmallScreen}
                      // Opening the sport dialog is safe; its billing controls
                      // still restrict an interrupted request to its original action.
                      isBillingActionLocked={type => !subForType(type)?.trialPendingAction && isBillingActionLocked(type)}
                    />
                  )}

                  {/* If all-access, show Lab inclusion info */}
                  {hasAllAccess && (
                    <Text fontSize="sm" color={mutedText}>
                      With ALL-ACCESS, you have unlimited access to every
                      endpoint across all sports, plus Lab, Webhooks, and
                      StoryStats. No need to manage individual sport
                      subscriptions.
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

          {/* ── Section 4: Account ─────────────────────────────── */}
          {isAccountPage && (
            <Box>
              <HStack mb={3}>
                <Heading size="sm" color={sectionHeaderColor} fontWeight="600">
                  Account
                </Heading>
                {isEnterprise && (
                  <Badge colorScheme="brand" borderRadius="md" px={2}>
                    Enterprise
                  </Badge>
                )}
              </HStack>
              <Divider borderColor={dividerBorderColor} mb={5} />

              <Box
                bg={cardBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={cardBorder}
                overflow="hidden"
              >
                {/* Email */}
                <Flex px={6} py={4} justify="space-between" align="center">
                  <Text fontSize="sm" color={mutedText}>
                    Email
                  </Text>
                  <Text fontSize="sm" fontWeight="500">
                    {user?.email}
                  </Text>
                </Flex>

                <Divider borderColor={dividerBorderColor} />

                {/* Tier */}
                {isEnterprise && (
                  <>
                    <Flex px={6} py={4} justify="space-between" align="center">
                      <Text fontSize="sm" color={mutedText}>
                        Tier
                      </Text>
                      <Badge colorScheme="brand" borderRadius="md" px={2}>
                        Enterprise
                      </Badge>
                    </Flex>

                    <Divider borderColor={dividerBorderColor} />
                  </>
                )}

                {/* Billing */}
                {!isEnterprise && (
                  <>
                    <Flex px={6} py={4} justify="space-between" align="center">
                      <Text fontSize="sm" color={mutedText}>
                        Payment Method
                      </Text>
                      {user?.stripeCustomerId ? (
                        <Link
                          fontSize="sm"
                          color="accent"
                          onClick={() => {
                            if (!hasOverlayBillingLock)
                              onManagePaymentDetails();
                          }}
                          _hover={{ textDecoration: "underline" }}
                          cursor={
                            hasOverlayBillingLock ? "not-allowed" : "pointer"
                          }
                          opacity={hasOverlayBillingLock ? 0.5 : 1}
                        >
                          Update payment or billing details{" "}
                          <ExternalLinkIcon mx="2px" boxSize={3} />
                        </Link>
                      ) : (
                        <Text fontSize="sm" color={mutedText}>
                          No payment method on file
                        </Text>
                      )}
                    </Flex>

                    <Divider borderColor={dividerBorderColor} />
                  </>
                )}

                {/* Support */}
                <Flex px={6} py={4} justify="space-between" align="center">
                  <Text fontSize="sm" color={mutedText}>
                    Support
                  </Text>
                  <Link
                    fontSize="sm"
                    href="mailto:hello@balldontlie.io"
                    color="accent"
                    _hover={{ textDecoration: "underline" }}
                  >
                    hello@balldontlie.io
                  </Link>
                </Flex>
              </Box>
            </Box>
          )}

          {isAccountPage && !isEnterprise && (
            <BillingHistory stripeCustomerId={user?.stripeCustomerId || null} />
          )}

          {/* Sport Detail Modal */}
          {isSubscriptionsPage && (
            <SportDetailModal
              isOpen={sportModalDisclosure.isOpen}
              onClose={() => {
                sportModalDisclosure.onClose();
                setSelectedSport(null);
              }}
              billingInterval={billingInterval}
              onBillingIntervalChange={setBillingInterval}
              currentBillingInterval={
                subscriptionDetails.find((d) => d.type === selectedSport)
                  ?.billingInterval || "month"
              }
              sport={selectedSport}
              currentTier={selectedSport ? getSubTier(selectedSport) : "free"}
              isPendingCancel={
                selectedSport
                  ? subscriptionDetails.some(
                      (d) => d.type === selectedSport && d.cancelAtPeriodEnd,
                    )
                  : false
              }
              cancelEndDate={
                selectedSport
                  ? (subscriptionDetails.find(
                      (d) => d.type === selectedSport && d.cancelAtPeriodEnd,
                    )?.currentPeriodEnd ?? null)
                  : null
              }
              onTierSelect={(tier) => {
                if (selectedSport) {
                  if (isStoryStatsType(selectedSport) && tier === "paid+") {
                    return;
                  }
                  setSelected({ tier, type: selectedSport });
                  sportModalDisclosure.onClose();
                }
              }}
              onReactivate={async () => {
                if (!selectedSport) return;
                await onReactivate(selectedSport);
                sportModalDisclosure.onClose();
              }}
              isTrialEligible={
                !!selectedSport && isGoatTrialEligibleFor(selectedSport)
              }
              onStartTrial={(type) => onRequestStartTrial(type, true)}
              isCurrentlyTrialing={
                !!selectedSport &&
                !!subForType(selectedSport) &&
                isTrialing(subForType(selectedSport)!)
              }
              trialPendingAction={selectedSport ? subForType(selectedSport)?.trialPendingAction : null}
              trialEnd={
                (selectedSport && subForType(selectedSport)?.trialEnd) || null
              }
              onTrialCancel={async (type) => {
                await onTrialCancel(type);
                sportModalDisclosure.onClose();
              }}
              onTrialReactivate={async (type) => {
                await onTrialReactivate(type);
                sportModalDisclosure.onClose();
              }}
              onTrialConvert={(type) => onRequestTrialConvert(type, true)}
              isBillingLocked={
                selectedSport ? isBillingActionLocked(selectedSport) : false
              }
              isTrialConversionPending={
                selectedSport ? isTrialConversionPending(selectedSport) : false
              }
            />
          )}
        </VStack>
      </Container>

      {trialCancellation && <TrialCancellationDialog
        key={`${trialCancellation.subscription.type}-${trialCancellation.action}`}
        subscription={trialCancellation.subscription}
        detail={subscriptionDetails.find(d => d.type === trialCancellation.subscription.type)}
        initialAction={trialCancellation.action} busy={cancellingTrial} error={trialCancellationError}
        onClose={() => setTrialCancellation(null)} onConfirm={confirmTrialCancellation}
      />}
      {isSubscriptionsPage && (
        <Modal isOpen={!!selected} onClose={() => setSelected(null)}>
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="xl" boxShadow="2xl">
            <ModalHeader>
              {selected?.tier === "free" ? "Cancel" : "Change"}{" "}
              {isAllAccessType(selected?.type ?? "")
                ? "ALL-ACCESS"
                : isStoryStatsType(selected?.type)
                  ? getStoryStatsProductInfo(selected?.type ?? "")?.displayName
                  : selected?.type.toUpperCase()}{" "}
              {selected?.tier === "free" ? "Subscription" : "Plan"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {annualChange ? (
                previewError ||
                (!planPreview ? (
                  "Loading price…"
                ) : (
                  <VStack align="start">
                    <Text>
                      {planPreview.deferred
                        ? "Changes at renewal"
                        : "Changes today"}
                    </Text>
                    <Text fontWeight="bold">
                      {billingPrice(
                        selected!.type,
                        selected!.tier,
                        billingInterval,
                      )}
                    </Text>
                    <Text>
                      Due today:{" "}
                      {(planPreview.amountDue / 100).toLocaleString("en-US", {
                        style: "currency",
                        currency: planPreview.currency,
                      })}
                    </Text>
                    <Text>
                      {planPreview.deferred
                        ? `Your current plan continues until ${new Date(planPreview.effectiveAt * 1000).toLocaleDateString()}.`
                        : "Includes credit for unused time. Your new plan starts after payment succeeds."}
                    </Text>
                  </VStack>
                ))
              ) : (
                <>
                  {modalContent()}
                  {selected &&
                    selected.tier !== "free" &&
                    !isStoryStatsType(selected.type) && (
                      <Text mt={3} fontWeight="bold">
                        {billingPrice(
                          selected!.type,
                          selected!.tier,
                          billingInterval,
                        )}
                      </Text>
                    )}
                </>
              )}
            </ModalBody>

            <ModalFooter>
              <HStack justify="space-between" w="100%">
                <Button
                  mr={3}
                  variant="ghost"
                  onClick={() => setSelected(null)}
                  isLoading={isLoading}
                >
                  <span>Close</span>
                </Button>
                <Button
                  variant="solid"
                  colorScheme={selected?.tier === "free" ? "red" : "brand"}
                  isLoading={isLoading}
                  isDisabled={
                    (!!selected && isBillingActionLocked(selected.type)) ||
                    (annualChange && !planPreview)
                  }
                  onClick={async () => {
                    if (selected && isBillingActionLocked(selected.type)) {
                      return;
                    }
                    setLoading(true);

                    try {
                      if (user?.stripeCustomerId) {
                        const response =
                          selected!.tier === "free"
                            ? await updateTier(selected!.tier, selected!.type)
                            : await updateTier(
                                selected!.tier,
                                selected!.type,
                                isStoryStatsType(selected!.type) ? undefined : billingInterval,
                                planPreview?.token,
                              );

                        if (response?.checkoutRequired) {
                          await onNewCustomer(selected!.tier, selected!.type);
                          return;
                        }

                        if (
                          response?.annualChange ||
                          response?.planChangeScheduled
                        ) {
                          let confirmed = true;
                          if (
                            !response.paymentPending &&
                            !response.planChangeScheduled &&
                            subForType(selected!.type)?.tier !== selected!.tier
                          ) {
                            const result = await pollForSubscriptionUpdate(
                              selected!.tier,
                              selected!.type,
                            );
                            setUser(result.user);
                            confirmed = result.confirmed;
                          } else {
                            setUser(await getSession());
                          }
                          toast({
                            title: response.planChangeScheduled
                              ? "Plan change scheduled"
                              : response.paymentPending
                                ? "Payment required"
                                : confirmed
                                  ? "Plan updated"
                                  : "Update In Progress",
                            description: response.paymentPending
                              ? "Complete payment to activate your new plan. Your current plan remains unchanged."
                              : response.planChangeScheduled
                                ? "Your current plan continues until renewal."
                                : confirmed
                                  ? "Your billing period has been updated."
                                  : "Your subscription change is being processed. It may take a few minutes to complete.",
                            status: response.paymentPending
                              ? "warning"
                              : confirmed ? "success" : "info",
                            duration: 8000,
                          });
                          if (response.paymentUrl)
                            window.location.assign(response.paymentUrl);
                          setSubscriptionDetails(
                            await getSubscriptionDetails(),
                          );
                        } else if (response?.scheduled) {
                          // End-of-period cancellation - skip polling
                          const endDate = new Date(
                            response.currentPeriodEnd,
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          });
                          toast({
                            title: "Cancellation Scheduled",
                            description: `Your subscription will remain active until ${endDate}.`,
                            status: "info",
                            isClosable: true,
                            duration: 8000,
                          });
                          // Refresh subscription details
                          getSubscriptionDetails()
                            .then(setSubscriptionDetails)
                            .catch(() => {});
                        } else {
                          // Immediate cancel or upgrade/downgrade - poll as before
                          const { user: updatedUser, confirmed } =
                            await pollForSubscriptionUpdate(
                              selected!.tier,
                              selected!.type,
                            );
                          setUser(updatedUser);
                          getSubscriptionDetails()
                            .then(setSubscriptionDetails)
                            .catch(() => {});

                          if (confirmed) {
                            toast({
                              title: "Subscription Updated",
                              description:
                                "Your subscription has been updated successfully.",
                              status: "success",
                              isClosable: true,
                              duration: 5000,
                            });
                          } else {
                            toast({
                              title: "Update In Progress",
                              description:
                                "Your subscription change is being processed. It may take a few minutes to complete.",
                              status: "info",
                              isClosable: true,
                              duration: null,
                            });
                          }
                        }
                      } else {
                        await onNewCustomer(selected!.tier, selected!.type);
                      }
                    } catch (e: any) {
                      const backendMessage = e?.response?.data?.error;
                      toast({
                        title: "Error",
                        description:
                          backendMessage ||
                          (user?.stripeCustomerId
                            ? "Manage your payment details and ensure a default payment method is selected."
                            : "Please contact hello@balldontlie.io for support."),
                        status: "error",
                        isClosable: true,
                        duration: null,
                      });
                    }

                    setLoading(false);
                    setSelected(null);
                  }}
                >
                  {/* Keep a React-owned element around browser-translated text. */}
                  <span>
                    {selected?.tier === "free"
                      ? "Cancel Subscription"
                      : "Confirm"}
                  </span>
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {isAccountPage && (
        <Modal
          isOpen={rotateDisclosure.isOpen}
          onClose={rotateDisclosure.onClose}
          closeOnOverlayClick={!isRotating}
          closeOnEsc={!isRotating}
        >
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="xl" boxShadow="2xl">
            <ModalHeader>Rotate API Key</ModalHeader>
            <ModalCloseButton isDisabled={isRotating} />
            <ModalBody>
              <Text>
                Are you sure you want to rotate your API key? This action is{" "}
                <Text as="span" fontWeight="bold">
                  irreversible
                </Text>
                . Your current API key will stop working immediately and all
                applications using it will need to be updated with the new key.
              </Text>
            </ModalBody>
            <ModalFooter>
              <HStack justify="space-between" w="100%">
                <Button
                  variant="ghost"
                  onClick={rotateDisclosure.onClose}
                  isDisabled={isRotating}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={isRotating}
                  onClick={onRotateApiKey}
                >
                  Rotate Key
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {isSubscriptionsPage && (
        <Modal
          isOpen={!!pendingTrialConversion}
          onClose={onDismissTrialConversion}
          closeOnOverlayClick={!isConfirmingTrialConversion}
          closeOnEsc={!isConfirmingTrialConversion}
        >
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="xl" boxShadow="2xl">
            <ModalHeader>Upgrade trial now?</ModalHeader>
            <ModalCloseButton isDisabled={isConfirmingTrialConversion} />
            <ModalBody>
              {pendingTrialConversionContent && (
                <VStack align="start" spacing={3}>
                  <Text>
                    This will end your trial immediately and convert you to the
                    full {pendingTrialConversionContent.packageName} package.
                  </Text>
                  <Text>
                    Your sports API rate limit will increase from{" "}
                    <Text as="span" fontWeight="bold">
                      {pendingTrialConversionContent.trialRateLimitLabel}
                    </Text>{" "}
                    to{" "}
                    <Text as="span" fontWeight="bold">
                      {pendingTrialConversionContent.fullRateLimitLabel}
                    </Text>
                    .
                  </Text>
                  <Text>{pendingTrialConversionContent.chargeDescription}</Text>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <HStack justify="space-between" w="100%">
                <Button
                  variant="ghost"
                  onClick={onDismissTrialConversion}
                  isDisabled={isConfirmingTrialConversion}
                >
                  Dismiss
                </Button>
                <Button
                  colorScheme="brand"
                  isLoading={isConfirmingTrialConversion}
                  onClick={onConfirmTrialConversion}
                >
                  Confirm upgrade
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {isSubscriptionsPage && (
        <Modal
          isOpen={!!pendingTrial}
          onClose={onCancelTrialConfirmation}
          closeOnOverlayClick={!isStartingTrial}
          closeOnEsc={!isStartingTrial}
        >
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="xl" boxShadow="2xl">
            <ModalHeader>Start 7-day free trial?</ModalHeader>
            <ModalCloseButton isDisabled={isStartingTrial} />
            <ModalBody>
              {pendingTrial && (
                <VStack align="start" spacing={3}>
                  <Text>You won't be charged for the next 7 days.</Text>
                  {subForType(pendingTrial.type)?.tier === "paid" ? (
                    <Text>
                      When the trial ends, your existing ALL-STAR subscription
                      will upgrade to GOAT at{" "}
                      {billingPrice(
                        pendingTrial.type,
                        "paid+",
                        billingInterval,
                      )}
                      .
                      {billingInterval === "year" &&
                      subscriptionDetails.find(
                        (d) => d.type === pendingTrial.type,
                      )?.billingInterval !== "year"
                        ? " A new annual term starts then, with credit for unused ALL-STAR time."
                        : " You pay the prorated difference for the rest of your current billing period and keep your renewal date."}{" "}
                      Choose Cancel at trial end or End trial now in your dashboard before the trial
                      ends to avoid being charged.
                    </Text>
                  ) : (
                    <Text>
                      When the trial ends, your card will be automatically
                      charged{" "}
                      <Text as="span" fontWeight="bold">
                        {billingPrice(
                          pendingTrial.type,
                          "paid+",
                          billingInterval,
                        )}
                      </Text>{" "}
                      and your subscription will renew{" "}
                      {billingInterval === "year" ? "annually" : "monthly"}. You
                      can cancel anytime in your dashboard before the trial ends
                      to avoid being charged.
                    </Text>
                  )}
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <HStack justify="space-between" w="100%">
                <Button
                  variant="ghost"
                  onClick={onCancelTrialConfirmation}
                  isDisabled={isStartingTrial}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="purple"
                  isLoading={isStartingTrial}
                  onClick={onConfirmTrial}
                >
                  Start trial
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};
